export type TopicStatus = 'verified' | 'debunked' | 'pending' | 'doubtful';

export interface TimelineItem {
  time: string;
  text: string;
}

export interface EvidenceItem {
  source: string;
  detail: string;
  weight: 'strong' | 'medium' | 'weak' | 'counter';
}

export interface AnalysisSection {
  heading: string;
  body: string;
}

export interface Topic {
  id: string;
  title: string;
  status: TopicStatus;
  categoryId: string;
  heat: number;
  date: string;
  author: string;
  summary: string;
  cover: string;
  coverAlt: string;
  tags: string[];
  claim: string;
  conclusion: string;
  timeline: TimelineItem[];
  evidence: EvidenceItem[];
  analysis: AnalysisSection[];
  related: string[];
}

export const statusMeta: Record<
  TopicStatus,
  { label: string; short: string; icon: string; tone: string; desc: string }
> = {
  verified: {
    label: '已求证属实',
    short: '已求证',
    icon: 'ri-checkbox-circle-fill',
    tone: 'accent',
    desc: '关键信息已获得交叉印证，核心说法成立',
  },
  debunked: {
    label: '已辟谣',
    short: '已辟谣',
    icon: 'ri-close-circle-fill',
    tone: 'primary',
    desc: '原始说法存在关键事实错误，已确认不成立',
  },
  pending: {
    label: '求证中',
    short: '求证中',
    icon: 'ri-loader-4-fill',
    tone: 'secondary',
    desc: '正在收集信源，结论尚未稳定，请谨慎转发',
  },
  doubtful: {
    label: '证据存疑',
    short: '存疑',
    icon: 'ri-question-fill',
    tone: 'secondary',
    desc: '现有材料无法支撑结论，存在明显拼接或断章取义',
  },
};

export const topics: Topic[] = [
  {
    id: 'airport-special-channel',
    title: '网传某选秀偶像享"机场特殊通道"待遇，工作人员被指偏袒',
    status: 'debunked',
    categoryId: 'entertainment',
    heat: 9860,
    date: '2026-08-28',
    author: '求证组 · 白露',
    summary:
      '一段十几秒的短视频被剪成"专属通道"叙事，我们把原片、机场当日流程单和现场其他旅客的素材放在同一条时间线上比对，结论和网传版本并不一致。',
    cover:
      'https://readdy.ai/api/search-image?query=editorial%20abstract%20illustration%20of%20airport%20corridor%20with%20warm%20cream%20background%2C%20muted%20amber%20and%20jade%20green%20geometric%20shapes%2C%20minimal%20flat%20vector%20style%2C%20soft%20paper%20texture%2C%20clean%20composition%2C%20no%20text&width=800&height=600&seq=gt-airport-01&orientation=landscape',
    coverAlt: '以暖色几何图形表现机场通道的插画',
    tags: ['娱乐', '行程爆料', '视频剪辑'],
    claim:
      '网传说法：某偶像在机场被安排使用"内部专用通道"，普通旅客被拦在两侧等候，现场安保态度强硬。',
    conclusion:
      '对比后发现，所谓"专用通道"是机场在客流高峰时段启用的临时分流通道，当天所有持商务舱及特殊服务凭证的旅客均可通行，并非为个人单独开放。原视频删去了排队广播与前段画面，属于剪辑误导。',
    timeline: [
      { time: '08-26 11:40', text: '短视频首发于某社交平台，配文"这通道是给谁开的？"' },
      { time: '08-26 14:05', text: '话题登上同城热搜，转发量快速上涨至两万' },
      { time: '08-27 09:15', text: '求证组联系到当日值班的地面服务人员，取得分流通道启用记录' },
      { time: '08-27 18:30', text: '完整版现场素材被其他旅客上传，可见排队广播与多组旅客通行画面' },
      { time: '08-28 10:00', text: '求证组发布结论：通道为客流分流措施，网传"专属待遇"不成立' },
    ],
    evidence: [
      {
        source: '机场地面服务排班表',
        detail: '当日 11:20 至 12:40 启用 C 区临时分流通道，适用于商务舱、特殊服务旅客',
        weight: 'strong',
      },
      {
        source: '完整现场视频（3 分 12 秒）',
        detail: '画面中先后有多组非艺人旅客经同一通道通行，广播反复提示分流规则',
        weight: 'strong',
      },
      {
        source: '首发短视频（14 秒）',
        detail: '仅保留通道口画面，删去广播与前段排队场景，时间戳与完整版相差 6 分钟',
        weight: 'counter',
      },
      {
        source: '机场客服公开口径',
        detail: '高峰时段分流为常规做法，不以旅客身份作为唯一判断依据',
        weight: 'medium',
      },
    ],
    analysis: [
      {
        heading: '为什么这段视频看起来"很像真的"',
        body:
          '误导型内容通常不需要伪造画面，只需要调整镜头顺序和时长。原片里先有广播、再有排队、最后才是通道画面，剪掉前两段后，"突然出现的专属通道"这个印象就被制造出来了。判断这类内容时，先找完整版，再看有没有前因。',
      },
      {
        heading: '被忽略的分流机制',
        body:
          '国内多数枢纽机场在早高峰、节假日都会启用临时分流，判断依据是客流压力和票务类别，而不是具体某个人。当我们把当天的客流曲线和通道启用时间叠在一起，逻辑就非常清楚：11 点 20 分正好是当日进港峰值。',
      },
      {
        heading: '给吃瓜群众的三个核对动作',
        body:
          '第一，看原视频时长是否异常短；第二，搜同一场景的其他旅客视角；第三，查机构方是否有公开说明。这三步做完，九成以上的"特殊待遇"类爆料都会露出破绽。',
      },
    ],
    related: ['celebrity-marriage-rumor', 'brand-collab-leak'],
  },
  {
    id: 'brand-collab-leak',
    title: '某饮品品牌与热门动漫联名被提前"泄露"，包装图疑似内部流出',
    status: 'pending',
    categoryId: 'business',
    heat: 7420,
    date: '2026-09-02',
    author: '求证组 · 老陈',
    summary:
      '几张排版工整的包装设计图在社群流传，看上去很像真的。我们比对品牌过往的设计规范与印刷工艺，发现其中两处细节对不上。',
    cover:
      'https://readdy.ai/api/search-image?query=editorial%20abstract%20illustration%20of%20product%20packaging%20mockup%20on%20warm%20cream%20paper%2C%20muted%20amber%20and%20deep%20jade%20geometric%20labels%2C%20minimal%20flat%20vector%20style%2C%20soft%20shadows%2C%20no%20text&width=800&height=600&seq=gt-brand-02&orientation=landscape',
    coverAlt: '暖色调包装设计插画',
    tags: ['商业', '联名', '设计稿'],
    claim: '网传说法：某饮品品牌将与热门动漫推出联名系列，附带六张包装设计图和一份上市时间表。',
    conclusion:
      '目前尚无品牌官方确认。设计图在字体间距与条码区排版上与品牌规范存在出入，时间表也被发现与去年同系列活动的节点高度重合。暂列为"求证中"，不建议当作已确定信息传播。',
    timeline: [
      { time: '08-30 20:10', text: '设计图首次出现在某品牌粉丝社群，附言"内部朋友发的"' },
      { time: '08-31 10:00', text: '求证组开始比对该品牌近三年包装设计规范' },
      { time: '09-01 15:20', text: '发现上市时间表与去年活动节点重合度超过 80%' },
      { time: '09-02 09:00', text: '品牌客服回应"暂未接到相关通知"，结论维持求证中' },
    ],
    evidence: [
      {
        source: '品牌设计规范文件（公开部分）',
        detail: '联名包装的条码区预留宽度为 22mm，图中标注约为 18mm，比例不符',
        weight: 'strong',
      },
      {
        source: '去年同系列活动时间表',
        detail: '预热、上线、加推三个节点的日期几乎一致，疑似直接套用旧表',
        weight: 'counter',
      },
      {
        source: '设计图本身',
        detail: '图层样式统一，但存在两处字体混用，专业设计方较少出现此类情况',
        weight: 'medium',
      },
      {
        source: '品牌客服',
        detail: '公开回复"暂未接到相关通知"，属于保留口径而非否认',
        weight: 'weak',
      },
    ],
    analysis: [
      {
        heading: '设计稿爆料为什么最难判断',
        body:
          '设计图不像视频，它没有环境音、没有前后画面，很难从"是不是完整"入手。更有效的方法是查规范：每个品牌都有较为稳定的字体、间距、留白比例，造假者往往能把整体做得像，却容易在毫米级的细节上露馅。',
      },
      {
        heading: '时间表是最容易造假的证据',
        body:
          '把一份"内部时间表"和品牌往年活动节点放在一起看，如果重合度高得离谱，基本可以判断是拿旧文件改的。真实的活动排期通常会因为供应链、审核周期产生浮动。',
      },
      {
        heading: '现在该怎么吃这个瓜',
        body:
          '归入"求证中"而不是"属实"或"辟谣"，是目前最诚实的做法。感兴趣的话可以关注品牌官方账号，联名如果真存在，一定会有预告物料，而不是只靠截图流传。',
      },
    ],
    related: ['ai-model-benchmark-leak', 'airport-special-channel'],
  },
  {
    id: 'ai-model-benchmark-leak',
    title: '网传某大模型"内部评测截图"流出，跑分远超同级别产品',
    status: 'doubtful',
    categoryId: 'tech',
    heat: 8110,
    date: '2026-08-21',
    author: '求证组 · 阿澈',
    summary:
      '一张带着测试表格的截图在开发者圈疯传。我们试着复现同一套测试集，结果与截图给出的分数差距明显。',
    cover:
      'https://readdy.ai/api/search-image?query=editorial%20abstract%20illustration%20of%20data%20chart%20and%20bars%20on%20warm%20cream%20background%2C%20muted%20amber%20line%20graph%20with%20jade%20green%20accents%2C%20minimal%20flat%20vector%20style%2C%20clean%20grid%2C%20no%20text&width=800&height=600&seq=gt-ai-03&orientation=landscape',
    coverAlt: '暖色数据图表插画',
    tags: ['科技', '评测', '跑分'],
    claim: '网传说法：一张标注"内部评测"的表格显示某大模型在多项基准测试中大幅领先，并附有提交时间。',
    conclusion:
      '截图中的测试集名称与实际公开版本不一致，条目数量也存在差异；我们用公开测试集复现，得分落在行业常规区间。判断为不可采信，标注"证据存疑"。',
    timeline: [
      { time: '08-19 22:30', text: '截图在开发者社群出现，配文"内部渠道"' },
      { time: '08-20 11:00', text: '多位开发者指出测试集名称拼写与公开版本不符' },
      { time: '08-20 21:40', text: '求证组按截图描述复现测试，记录实际得分区间' },
      { time: '08-21 16:00', text: '综合比对后标注为"证据存疑"' },
    ],
    evidence: [
      {
        source: '公开测试集文档',
        detail: '截图中的测试集条目为 8600 条，公开版本为 10240 条，数量对不上',
        weight: 'strong',
      },
      {
        source: '复现测试记录',
        detail: '在相同提示词下，得分处于该类产品的常见波动区间内',
        weight: 'strong',
      },
      {
        source: '截图本身',
        detail: '表格存在两处数字右对齐不一致，疑似后期编辑',
        weight: 'medium',
      },
      {
        source: '所谓"提交时间"',
        detail: '对应时间点该平台并无相关公开提交记录',
        weight: 'medium',
      },
    ],
    analysis: [
      {
        heading: '跑分截图的可信度取决于什么',
        body:
          '评测结论要能成立，至少需要三件事：测试集可查、提示词可复现、结果可重复。只给一张最终表格，却不给输入和运行环境，这在方法上就已经不完整了。',
      },
      {
        heading: '为什么复现是有效的破局方式',
        body:
          '与其争论截图真假，不如按它描述的条件跑一遍。跑分与实际差异过大，说明要么测试条件被改动过，要么数据本身是拼的。复现得到的数字，比任何口头辩论都更有说服力。',
      },
      {
        heading: '面对技术爆料的心态',
        body:
          '技术圈的信息更新很快，但对"跑分遥遥领先"这类说法保持一点延迟满足是好事。等到正式发布、有第三方复现之后再下判断，往往能省掉一次被打脸的尴尬。',
      },
    ],
    related: ['game-update-leak', 'brand-collab-leak'],
  },
  {
    id: 'metro-free-ride',
    title: '"地铁将全面免费乘车"截图流传，落款单位并不存在',
    status: 'debunked',
    categoryId: 'society',
    heat: 9230,
    date: '2026-08-15',
    author: '求证组 · 白露',
    summary:
      '一张红头文件截图被大量转发。我们从落款、文号格式和发文日期三个角度核对，发现它几乎每一处都经不起推敲。',
    cover:
      'https://readdy.ai/api/search-image?query=editorial%20abstract%20illustration%20of%20city%20metro%20map%20lines%20on%20warm%20cream%20background%2C%20muted%20amber%20routes%20and%20jade%20green%20stations%2C%20minimal%20flat%20vector%20style%2C%20no%20text&width=800&height=600&seq=gt-metro-04&orientation=landscape',
    coverAlt: '暖色地铁线路插画',
    tags: ['社会', '政策截图', '红头文件'],
    claim: '网传说法：一张盖有公章的"通知"截图称，某市地铁将于下月起全面实行免费乘车。',
    conclusion:
      '截图落款单位名称与当地实际机构名称不符，文号编码规则错误，且缺少正规公文的版记格式。已确认该文件不存在，属于伪造内容。',
    timeline: [
      { time: '08-13 19:00', text: '截图在本地社群流传，被描述为"内部渠道"' },
      { time: '08-14 09:30', text: '求证组核对当地机构公开名录，未找到截图落款单位' },
      { time: '08-14 15:10', text: '发现文号格式与当地区号编码规则不符' },
      { time: '08-15 08:00', text: '发布结论：文件系伪造，提醒勿轻信' },
    ],
    evidence: [
      {
        source: '当地政府机构名录',
        detail: '截图中落款单位名称不存在，与相近机构名称相差两个字',
        weight: 'strong',
      },
      {
        source: '公文格式规范',
        detail: '截图中文号缺少年份括号格式，版记部分缺失，不符合正式公文要求',
        weight: 'strong',
      },
      {
        source: '当地交通部门公开信息',
        detail: '近期票价政策无调整公告，客服口径为"以官方发布为准"',
        weight: 'medium',
      },
      {
        source: '截图本身',
        detail: '公章边缘像素化明显，与其他文字层级压缩程度不一致',
        weight: 'counter',
      },
    ],
    analysis: [
      {
        heading: '红头文件为什么总被当成"实锤"',
        body:
          '红头文件自带权威感，很多人看到红色标题就默认可信。实际上，伪造一份排版像样的文件只需几分钟。真正需要核对的是那些容易被忽略的部分：文号、落款、版记、印章位置。',
      },
      {
        heading: '三个三十秒就能完成的核对',
        body:
          '查机构名字是否真实存在；查文号是否符合规范格式；去官方渠道搜有没有对应公告。三步都不需要专业知识，却能过滤掉大量假文件。',
      },
      {
        heading: '关于"免费"这类好事型谣言',
        body:
          '越是好消息，转发意愿越强，核对意愿越弱。造谣者也很清楚这一点。遇到"免费""补贴""大幅降价"类文件，多等一天官方口径，不吃亏。',
      },
    ],
    related: ['school-grad-rumor', 'airport-special-channel'],
  },
  {
    id: 'game-update-leak',
    title: '热门游戏新版本角色提前泄露，多处细节与官方预告吻合',
    status: 'verified',
    categoryId: 'tech',
    heat: 8650,
    date: '2026-08-09',
    author: '求证组 · 阿澈',
    summary:
      '这次的爆料和以往不太一样：技能图标、语音条数和版本号，都能在后续官方信息里找到对应。我们把交集部分逐条列了出来。',
    cover:
      'https://readdy.ai/api/search-image?query=editorial%20abstract%20illustration%20of%20game%20interface%20cards%20on%20warm%20cream%20background%2C%20muted%20amber%20and%20jade%20green%20panels%2C%20minimal%20flat%20vector%20style%2C%20soft%20depth%2C%20no%20text&width=800&height=600&seq=gt-game-05&orientation=landscape',
    coverAlt: '暖色游戏界面卡片插画',
    tags: ['科技', '游戏', '版本泄露'],
    claim: '网传说法：一份标为新版本的角色资料表流出，包含技能描述、语音数量与上线版本号。',
    conclusion:
      '该资料表中的角色名、技能图标数量、语音条目与官方后续公布的信息高度一致，版本号也对得上。判断核心内容属实，但部分数值在上线前被调整，不能作为最终定稿使用。',
    timeline: [
      { time: '07-30 21:00', text: '资料表在玩家论坛流出，附少量图标截图' },
      { time: '08-02 12:00', text: '求证组整理资料表关键字段，建立待核对清单' },
      { time: '08-06 18:00', text: '官方预告发布，角色名与技能图标数量与资料表一致' },
      { time: '08-09 09:30', text: '确认版本号吻合，标注"已求证属实"' },
    ],
    evidence: [
      {
        source: '官方版本预告',
        detail: '角色名、技能图标数量与资料表完全对应，误差为 0',
        weight: 'strong',
      },
      {
        source: '语音包条目数',
        detail: '资料表标注 42 条，预告片中可辨识的语音节点与之吻合',
        weight: 'strong',
      },
      {
        source: '版本号',
        detail: '资料表标注的版本号与官方预告一致',
        weight: 'medium',
      },
      {
        source: '数值字段',
        detail: '部分技能数值与上线版本存在差异，属于上线前常规调整',
        weight: 'counter',
      },
    ],
    analysis: [
      {
        heading: '"属实"不等于"全部准确"',
        body:
          '一份爆料可以整体属实、局部失准。这次资料表中的结构、命名、数量都对得上，但数值在上线前被改动过。把它当成"准确"，和把它当成"完全定稿"，是两件事。',
      },
      {
        heading: '交叉印证的关键是找独立交集',
        body:
          '我们不去证明整份文件是真的，而是找出那些"如果文件是假的就不可能同时出现"的细节。当多个独立细节都能对应，可信度才会上升。',
      },
      {
        heading: '玩家该怎么使用这类信息',
        body:
          '提前知道版本方向，确实能帮助规划资源。但涉及具体数值和上线时间，仍要以正式公告为准，避免因为提前投入造成损失。',
      },
    ],
    related: ['ai-model-benchmark-leak', 'athlete-transfer-rumor'],
  },
  {
    id: 'cream-cost-five-yuan',
    title: '"某网红面霜成本只要五块钱"？我们把成分表和原料报价摊开算了一遍',
    status: 'doubtful',
    categoryId: 'consumer',
    heat: 6890,
    date: '2026-08-25',
    author: '求证组 · 老陈',
    summary:
      '这类说法每隔一段时间就会出现一次。它把原料成本当成了全部成本，也忽略了配方浓度，直接得出"暴利"结论，逻辑上并不成立。',
    cover:
      'https://readdy.ai/api/search-image?query=editorial%20abstract%20illustration%20of%20skincare%20jar%20and%20ingredient%20circles%20on%20warm%20cream%20background%2C%20muted%20amber%20and%20jade%20green%20shapes%2C%20minimal%20flat%20vector%20style%2C%20no%20text&width=800&height=600&seq=gt-cream-06&orientation=landscape',
    coverAlt: '暖色护肤品成分插画',
    tags: ['消费', '成分', '成本拆解'],
    claim: '网传说法：某热销面霜的核心原料成本不足五元，售价却接近三百元，属于"智商税"。',
    conclusion:
      '按公开原料报价与配方浓度估算，原料成本确实不高，但"五元"忽略了配方中占比更高的辅料、包材、备案检测与渠道成本。结论被简化为"暴利"，属于证据存疑。',
    timeline: [
      { time: '08-22 20:00', text: '短视频发布，用原料报价单直接得出"成本五元"结论' },
      { time: '08-23 14:00', text: '求证组获取公开成分表，按浓度区间估算原料成本' },
      { time: '08-24 11:00', text: '补充包材、备案检测、渠道分成等成本项' },
      { time: '08-25 10:00', text: '发布结论：成本说法以偏概全，标注存疑' },
    ],
    evidence: [
      {
        source: '公开成分表',
        detail: '核心活性成分位于成分表第 9 位，按常规浓度估算其原料成本约 6 至 12 元',
        weight: 'strong',
      },
      {
        source: '原料供应商公开报价',
        detail: '报价为吨级采购价，小批量生产时单位成本会明显上升',
        weight: 'medium',
      },
      {
        source: '成本结构常识',
        detail: '包材、备案检测、仓储物流与渠道费用常占售价的较大比例',
        weight: 'strong',
      },
      {
        source: '原短视频',
        detail: '仅展示单一原料报价，未说明配方浓度与采购量，结论跳跃',
        weight: 'counter',
      },
    ],
    analysis: [
      {
        heading: '把原料成本等同于产品成本，是最常见的偷换',
        body:
          '一瓶护肤品从原料到上架，中间还有配方研发、稳定性测试、备案、包装、仓储与渠道。只挑最便宜的那一环来讲，结论当然耸动，但它描述的不是完整成本。',
      },
      {
        heading: '浓度决定了成本能差多少倍',
        body:
          '同样一个成分，添加 0.1% 和 5% 的成本可以差几十倍。脱离浓度谈原料成本，就像只报菜价不问份量，数字再具体也没有意义。',
      },
      {
        heading: '理性消费该看什么',
        body:
          '与其纠结"成本几块钱"，不如关注成分是否适合自己、浓度是否在有效区间、有没有合规备案。这些才是和实际体验真正相关的信息。',
      },
    ],
    related: ['metro-free-ride', 'brand-collab-leak'],
  },
  {
    id: 'athlete-transfer-rumor',
    title: '明星球员转会传闻持续升温，多个独立信源指向同一家俱乐部',
    status: 'verified',
    categoryId: 'sports',
    heat: 7340,
    date: '2026-07-28',
    author: '求证组 · 老陈',
    summary:
      '转会窗口期的传闻最多也最难辨。我们只保留那些有独立信源、且时间上能相互支撑的信息，剔除掉明显的二手转述。',
    cover:
      'https://readdy.ai/api/search-image?query=editorial%20abstract%20illustration%20of%20stadium%20arc%20and%20jersey%20number%20on%20warm%20cream%20background%2C%20muted%20amber%20and%20jade%20green%20geometric%20shapes%2C%20minimal%20flat%20vector%20style%2C%20no%20text&width=800&height=600&seq=gt-sport-07&orientation=landscape',
    coverAlt: '暖色球场与球衣插画',
    tags: ['体育', '转会', '信源'],
    claim: '网传说法：某明星球员将在新赛季转会至某俱乐部，双方已就个人条款达成一致。',
    conclusion:
      '三家相互独立的体育媒体在同一天给出了方向一致的报道，且其中一家提供了合同年限的具体信息。结合俱乐部注册窗口安排，判断转会方向属实，但个人条款细节尚无官方确认。',
    timeline: [
      { time: '07-24 08:00', text: '第一家体育媒体发布转会传闻，未提供具体条款' },
      { time: '07-25 20:00', text: '第二家媒体补充合同年限信息' },
      { time: '07-26 13:00', text: '第三家媒体从俱乐部侧确认接触存在' },
      { time: '07-28 10:00', text: '求证组比对三条独立信源时间线后标注属实' },
    ],
    evidence: [
      {
        source: '体育媒体 A',
        detail: '首报转会接触，未提及合同细节，属于方向性信息',
        weight: 'medium',
      },
      {
        source: '体育媒体 B',
        detail: '给出合同年限与体检安排，信息颗粒度明显更细',
        weight: 'strong',
      },
      {
        source: '体育媒体 C',
        detail: '从俱乐部侧确认谈判推进，与 A、B 时间线不冲突',
        weight: 'strong',
      },
      {
        source: '球员本人社交账号',
        detail: '暂未回应，属于常见状态，不构成反证',
        weight: 'weak',
      },
    ],
    analysis: [
      {
        heading: '转会传闻的可信度怎么排序',
        body:
          '只报"有意向"的最容易写，也最不可信；能给出合同年限、体检安排、解约金结构这类细节的，通常信源更接近谈判桌。细节越具体，编造的成本越高。',
      },
      {
        heading: '独立信源比数量更重要',
        body:
          '如果三家媒体其实都在转述同一个来源，那还是"一家之言"。我们逐个核对发布时间与信息颗粒度，确认它们各自独立，这样可信度才会真正叠加。',
      },
      {
        heading: '"方向属实"和"板上钉钉"的距离',
        body:
          '转会谈到一半告吹的情况并不少见。方向可信，不等于结果确定。关注这类消息时，保留一点余地总是稳妥的。',
      },
    ],
    related: ['game-update-leak', 'celebrity-marriage-rumor'],
  },
  {
    id: 'school-grad-rumor',
    title: '"某高校取消保研"截图疯传，实为旧通知被改标题',
    status: 'debunked',
    categoryId: 'society',
    heat: 8120,
    date: '2026-08-05',
    author: '求证组 · 白露',
    summary:
      '截图里的正文内容其实是三年前的一份调整通知，有人换了标题和日期重新发了出来。找到原文之后，事情就很清楚了。',
    cover:
      'https://readdy.ai/api/search-image?query=editorial%20abstract%20illustration%20of%20documents%20and%20magnifier%20on%20warm%20cream%20background%2C%20muted%20amber%20papers%20with%20jade%20green%20accents%2C%20minimal%20flat%20vector%20style%2C%20no%20text&width=800&height=600&seq=gt-school-08&orientation=landscape',
    coverAlt: '暖色文件与放大镜插画',
    tags: ['社会', '教育', '旧闻新炒'],
    claim: '网传说法：截图显示某高校发布通知，将取消全部推荐免试研究生资格。',
    conclusion:
      '截图正文与三年前该校一份局部调整通知内容一致，但标题和日期被替换。该校今年的推免工作正常开展，网传"取消保研"不成立。',
    timeline: [
      { time: '08-03 16:00', text: '改标题的截图在学生社群流传' },
      { time: '08-04 10:00', text: '求证组在高校官网历史公告中找到相同正文' },
      { time: '08-04 18:00', text: '核对发布日期，确认为三年前旧通知' },
      { time: '08-05 09:00', text: '发布辟谣结论，贴出原文链接' },
    ],
    evidence: [
      {
        source: '高校官网历史公告',
        detail: '相同正文可追溯至三年前，原文为局部流程调整，与"取消保研"无关',
        weight: 'strong',
      },
      {
        source: '该校今年推免安排',
        detail: '官网正常公布推免工作日程，流程未取消',
        weight: 'strong',
      },
      {
        source: '截图与原文对比',
        detail: '标题与日期被替换，正文一字未改，属于典型旧闻改造',
        weight: 'counter',
      },
      {
        source: '在校学生反馈',
        detail: '学院群内已澄清，多数学生未受影响',
        weight: 'medium',
      },
    ],
    analysis: [
      {
        heading: '旧闻新炒为什么屡试不爽',
        body:
          '因为原文是真的。造谣者不需要编内容，只要换一个更刺激的标题和更新的日期，再配上模糊截图，就能让人第一眼相信。核对的突破口在于找到原文的发布时间。',
      },
      {
        heading: '查原文比争论截图更有效',
        body:
          '搜索正文中的独特表述，往往能直接命中原始公告。这一步在很多情况下只需要一分钟，却能立刻把讨论从"是不是真的"变成"这是哪一年的事"。',
      },
      {
        heading: '涉及切身利益的信息更要等一等',
        body:
          '升学、考试、政策这类消息直接牵动情绪，也最容易被加工。看到令人焦虑的截图，先找官方渠道原文，再决定要不要紧张，能省掉很多不必要的慌乱。',
      },
    ],
    related: ['metro-free-ride', 'cream-cost-five-yuan'],
  },
  {
    id: 'celebrity-marriage-rumor',
    title: '两位艺人的婚变爆料反复登上热搜，目前只有模糊时间线',
    status: 'pending',
    categoryId: 'entertainment',
    heat: 9570,
    date: '2026-09-01',
    author: '求证组 · 阿澈',
    summary:
      '这条热搜的所有材料都指向同一组偷拍照片，却没有任何一方出面说明。我们把它列为"求证中"，并说明为什么现在下结论为时过早。',
    cover:
      'https://readdy.ai/api/search-image?query=editorial%20abstract%20illustration%20of%20two%20overlapping%20photo%20frames%20on%20warm%20cream%20background%2C%20muted%20amber%20and%20jade%20green%20shapes%2C%20minimal%20flat%20vector%20style%2C%20soft%20grain%2C%20no%20text&width=800&height=600&seq=gt-celebrity-09&orientation=landscape',
    coverAlt: '暖色相框交叠插画',
    tags: ['娱乐', '婚变', '偷拍'],
    claim: '网传说法：两位艺人的婚姻出现状况，配图为两人近期未同时出现的对比照片。',
    conclusion:
      '所有爆料都源自同一组对比照片，缺乏独立信源；艺人双方及经纪公司均未回应。在信息不足的情况下，我们不支持任何一侧的结论，维持"求证中"。',
    timeline: [
      { time: '08-29 21:00', text: '对比照片首次发布，配文使用大量推测性表述' },
      { time: '08-30 10:00', text: '话题进入热搜，出现多个二次解读账号' },
      { time: '08-31 16:00', text: '求证组尝试联系双方经纪团队，暂未获得回复' },
      { time: '09-01 11:00', text: '结论维持求证中，提醒勿传播未证实内容' },
    ],
    evidence: [
      {
        source: '首发照片组',
        detail: '照片本身为公开活动图拼接，未提供拍摄时间与地点的一致性说明',
        weight: 'weak',
      },
      {
        source: '二次解读账号',
        detail: '多个账号内容高度雷同，判断为同一来源的分发',
        weight: 'counter',
      },
      {
        source: '双方公开行程',
        detail: '近期行程确有不重叠，但艺人行程不重叠属于常态，不构成证据',
        weight: 'weak',
      },
      {
        source: '经纪方回应',
        detail: '截至发稿未回应，不能解读为默认',
        weight: 'medium',
      },
    ],
    analysis: [
      {
        heading: '为什么我们不急着下结论',
        body:
          '婚变类爆料的证据往往最薄弱，却传播得最快。当所有材料都来自同一组照片、同一个账号，它就不能算作"多方印证"。保持求证中，是对当事人也是对自己负责。',
      },
      {
        heading: '"没回应"不等于"默认"',
        body:
          '很多团队对私生活传闻选择不回应，这是常见做法。把沉默当成承认，是一种逻辑跳跃，也容易在事后陷入尴尬。',
      },
      {
        heading: '吃瓜的边界在哪',
        body:
          '关注公开信息没有问题，但传播拼接照片、揣测私人生活，可能会对无关的人造成伤害。理性的吃瓜方式，是把注意力放在可核实的事实上。',
      },
    ],
    related: ['airport-special-channel', 'athlete-transfer-rumor'],
  },
];